/**
 * Turn Google Calendar Phase 2b — Card d'intégration Google Calendar dans Settings.
 *
 * États :
 *  - Non connecté : bouton "Connecter Google Calendar" → appelle
 *    `GET /integrations/google/authorize` (JSON) et redirige vers l'URL Google.
 *  - Connecté    : email affiché + toggle `sync_enabled` (PATCH `sync-toggle`)
 *    + bouton "Déconnecter".
 *  - needs_reconnect : bandeau warning + bouton "Reconnecter".
 *
 * Gère les query params retour callback `?google_status=connected&email=...`
 * ou `?google_status=error&reason=...` → toast puis clean URL.
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Calendar as CalendarIcon, AlertTriangle, CheckCircle2 } from "lucide-react";

const ERROR_REASONS = {
  invalid_state: "Session expirée, réessaie.",
  state_expired: "Session expirée, réessaie.",
  calendar_scope_missing:
    "Coche bien l'accès Calendar sur l'écran Google (permission requise).",
  token_exchange_failed: "Erreur d'échange avec Google, réessaie.",
  no_refresh_token:
    "Google n'a pas renvoyé de refresh token. Retire l'accès depuis ton compte Google puis reconnecte.",
  internal_error: "Erreur inattendue, contacte le support.",
  access_denied: "Autorisation refusée sur Google.",
  missing_params: "Retour Google invalide, réessaie.",
};

const DEFAULT_STATUS = {
  connected: false,
  google_email: null,
  sync_enabled: false,
  last_sync_status: "not_connected",
  needs_reconnect: false,
};

export default function GoogleCalendarCard() {
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setStatus({ ...DEFAULT_STATUS, ...data });
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de charger le statut Google Calendar"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Gère les query params du callback OAuth
    const params = new URLSearchParams(window.location.search);
    const gStatus = params.get("google_status") || params.get("status");
    if (gStatus === "connected") {
      const email = params.get("email") || "";
      toast.success(
        email
          ? `Google Calendar connecté (${email})`
          : "Google Calendar connecté"
      );
    } else if (gStatus === "error") {
      const reason = params.get("reason") || "";
      toast.error(ERROR_REASONS[reason] || `Erreur Google (${reason || "inconnue"})`);
    }
    if (gStatus) {
      // Nettoie l'URL sans recharger
      params.delete("google_status");
      params.delete("status");
      params.delete("email");
      params.delete("reason");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : "")
      );
    }
  }, [fetchStatus]);

  const startConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/google/authorize");
      if (data?.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        toast.error("URL Google OAuth introuvable dans la réponse");
        setBusy(false);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de démarrer la connexion Google"));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      "Déconnecter Google Calendar ? Les événements déjà créés resteront dans ton agenda, mais Wérko ne synchronisera plus les prochains RDV."
    )) return;
    setBusy(true);
    try {
      await api.delete("/integrations/google/disconnect");
      toast.success("Google Calendar déconnecté");
      await fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err, "Erreur lors de la déconnexion"));
    } finally {
      setBusy(false);
    }
  };

  const toggleSync = async (enabled) => {
    // Optimistic
    setStatus((s) => ({ ...s, sync_enabled: enabled }));
    try {
      await api.patch("/integrations/google/sync-toggle", { sync_enabled: enabled });
      toast.success(enabled ? "Synchronisation activée" : "Synchronisation mise en pause");
    } catch (err) {
      // Rollback
      setStatus((s) => ({ ...s, sync_enabled: !enabled }));
      toast.error(formatApiError(err, "Impossible de changer la synchro"));
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-800 bg-slate-900/40" data-testid="google-calendar-card">
        <CardContent className="flex items-center gap-2 p-4 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </CardContent>
      </Card>
    );
  }

  // --- État NON CONNECTÉ ------------------------------------------------
  if (!status.connected) {
    return (
      <Card className="border-slate-800 bg-slate-900/40" data-testid="google-calendar-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-[#C4553B]/15 p-2 text-[#C4553B]">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-100">Google Calendar</h3>
              <p className="mt-1 text-sm text-slate-400">
                Synchronise automatiquement les rendez-vous Wérko vers ton agenda Google
                personnel. Le client n'est pas invité.
              </p>
              <button
                type="button"
                onClick={startConnect}
                disabled={busy}
                className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#C4553B] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#A8402C] disabled:opacity-60"
                data-testid="google-calendar-connect-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />}
                Connecter Google Calendar
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- État CONNECTÉ ----------------------------------------------------
  return (
    <div className="space-y-3" data-testid="google-calendar-card">
      {status.needs_reconnect && (
        <div
          className="flex items-start gap-3 rounded-lg border border-[#D99A2B]/60 bg-[#D99A2B]/10 p-3"
          data-testid="google-calendar-reconnect-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#D99A2B]" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-slate-100">Ton accès Google a expiré</div>
            <p className="mt-0.5 text-slate-400">
              Reconnecte-toi pour continuer à synchroniser les RDV.
            </p>
            <button
              type="button"
              onClick={startConnect}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-2 rounded-md bg-[#C4553B] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#A8402C] disabled:opacity-60"
              data-testid="google-calendar-reconnect-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarIcon className="h-3.5 w-3.5" />}
              Reconnecter
            </button>
          </div>
        </div>
      )}

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-emerald-500/15 p-2 text-emerald-500">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-slate-100">Google Calendar</h3>
                <span
                  className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-400"
                  data-testid="google-calendar-connected-badge"
                >
                  Connecté
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-400">
                Compte : <span className="font-medium text-slate-200" data-testid="google-calendar-email">{status.google_email || "—"}</span>
              </p>

              <div className="mt-3 flex items-center justify-between gap-4 rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-100">Synchronisation activée</div>
                  <div className="text-xs text-slate-500">
                    {status.sync_enabled
                      ? "Les RDV créés/modifiés/annulés apparaîtront dans ton agenda Google."
                      : "Synchronisation en pause. Les RDV ne remontent pas vers Google."}
                  </div>
                </div>
                <Switch
                  checked={status.sync_enabled}
                  onCheckedChange={toggleSync}
                  disabled={busy || status.needs_reconnect}
                  data-testid="google-calendar-sync-toggle"
                />
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  onClick={disconnect}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-60"
                  data-testid="google-calendar-disconnect-btn"
                >
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Déconnecter
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
